import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from '@tmapy/redux';

import { Autocomplete, FormItem } from '@tmapy/style-guide';

import { useWhisperer } from '../../hooks';
import type { ServerErrorItem } from '../../hooks/useFormItemServerErrors';

export interface ILocationItem {
  location: string | null;
}

export const LocationWhisperer: React.FC<{
  label: string;
  errorMessage: string;
  serverError: ServerErrorItem | null;
  value: string;
  validate: boolean;
  formItemChangeCallback(): void;
  onChange(e: React.MouseEvent | React.ChangeEvent, data: { ident: string; value: string }[]): void;
}> = React.memo(
  ({ label, errorMessage, serverError, value, validate, formItemChangeCallback, onChange }) => {
    const services = useSelector((state) => state.app.services);
    const [locationItems, setLocationItems] = useState<ILocationItem[]>([]);
    const [location, setLocation] = useState<string>(value);
    const isLocationInvalid = useRef(false);

    const fetchRuianData = useWhisperer({
      getUrl: (value: string) => `${services.suggestParcel}&singleLine=${value}`,
      setItems: (data: any) => {
        let locationItems: ILocationItem[] = [];
        if (data?.candidates?.length) {
          locationItems = data.candidates.map(
            (item: any) =>
              ({
                location: item.address,
              }) as ILocationItem,
          );
        }
        setLocationItems(locationItems);
      },
      sendAuth: false,
    });

    const handleSuggestionClick = (
      e: React.MouseEvent,
      eventData: { item: ILocationItem; name: string },
    ) => {
      const value = eventData.item.location;

      setLocation(value || '');
      onChange(e, [{ ident: 'location', value: value || '' }]);
    };

    const handleAutocompleteChange = (
      e: React.ChangeEvent,
      eventData: { name: string; value: string },
    ) => {
      formItemChangeCallback();
      fetchRuianData(eventData.value);
      setLocation(eventData.value);
      onChange(e, [{ ident: eventData.name, value: eventData.value }]);
    };

    const renderLocationItem = (item: ILocationItem) => <>{item.location}</>;

    useEffect(
      () => () => {
        fetchRuianData.cancel();
      },
      [fetchRuianData],
    );

    if (validate || isLocationInvalid.current) {
      isLocationInvalid.current = !location;
    }

    return (
      <FormItem
        label={label}
        isRequired
        errorMessage={isLocationInvalid.current ? errorMessage : null}
        {...serverError}
      >
        <Autocomplete
          name='location'
          value={location}
          items={locationItems}
          isInvalid={isLocationInvalid.current}
          renderItem={renderLocationItem}
          onChange={handleAutocompleteChange}
          onItemClick={handleSuggestionClick}
          {...serverError}
        />
      </FormItem>
    );
  },
);
